import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlayIcon } from '@heroicons/react/24/outline';

const VideoIntro = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoId = 'mH_zD02X9Ps';
  
  return (
    <div className="bg-gradient-to-br from-dark-800 to-dark-900 py-24 sm:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary-500">See AI Doctor in Action</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Revolutionizing Healthcare with AI
            </p>
            <p className="mt-6 text-lg leading-8 text-primary-200">
              Watch how our AI Doctor transforms patient care with intelligent, offline medical assistance 
              that's always available when you need it most.
            </p>
          </motion.div>
        </div>
        
        <div className="mt-16 relative z-10">
          <motion.div 
            className="relative mx-auto max-w-4xl"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="aspect-w-16 aspect-h-9 rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
              {!isPlaying ? (
                <motion.div 
                  className="relative h-[400px] bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center cursor-pointer"
                  onClick={() => setIsPlaying(true)}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="absolute inset-0 bg-black/20"></div>
                  <motion.div 
                    className="relative text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    <motion.div 
                      className="mx-auto h-20 w-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <PlayIcon className="h-8 w-8 text-white" />
                    </motion.div>
                    <p className="mt-4 text-white text-lg font-medium">Click to play video</p>
                  </motion.div>
                  <img 
                    src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                    alt="AI Doctor Video Thumbnail"
                    className="absolute inset-0 w-full h-full object-cover opacity-50"
                  />
                </motion.div>
              ) : (
                <iframe
                  className="w-full h-[400px]"
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                  title="AI Doctor Video Introduction"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              )}
            </div>
          </motion.div>
          
          <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3">
            {[
              {
                title: "Accurate Diagnosis",
                description: "Advanced AI algorithms provide reliable medical insights based on symptoms and history.",
                icon: (
                  <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
              {
                title: "Complete Privacy",
                description: "Your medical data never leaves your device. HIPAA compliant offline processing.",
                icon: (
                  <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                ),
              },
              {
                title: "Instant Access",
                description: "Get medical assistance 24/7 without internet. Perfect for emergencies and remote areas.",
                icon: (
                  <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
              },
            ].map((feature, index) => (
              <motion.div 
                key={feature.title}
                className="text-center p-6 rounded-xl bg-dark-800/50 backdrop-blur-sm shadow-lg hover:shadow-xl transition-shadow border border-primary-900/20"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <motion.div 
                  className="mx-auto h-12 w-12 rounded-lg bg-primary-500/20 flex items-center justify-center mb-4 border border-primary-500/30"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  {feature.icon}
                </motion.div>
                <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
                <p className="mt-2 text-base text-primary-200">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoIntro;
